/**
 * 크레딧 관리 대시보드
 *
 *  최근 30일 핵심 지표 + TOP 사업장 + 카테고리별 사용 분포.
 *  관리자가 가격 정책 변경 의사결정 전에 참고할 운영 현황.
 */
import { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Repeat } from 'lucide-react';
import { creditApi, CreditStats } from '@/services/api/credit';

const formatKrw = (n: number) => `₩${n.toLocaleString()}`;
const formatNum = (n: number) => n.toLocaleString();

export function CreditDashboardPage() {
  const [stats, setStats] = useState<CreditStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    creditApi.getStats().then((s) => {
      setStats(s);
      setLoading(false);
    });
  }, []);

  if (loading || !stats) {
    return (
      <div className="p-6">
        <div className="h-32 animate-pulse rounded-lg bg-gray-100" />
      </div>
    );
  }

  const netRevenue = stats.purchaseRevenueKrw + stats.paymentFeeRevenueKrw;

  return (
    <div className="p-6 space-y-6">
      {/* 헤더 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">크레딧 현황</h1>
        <p className="mt-1 text-sm text-gray-500">
          {stats.periodFrom} ~ {stats.periodTo} · 크레딧 운영 현황
        </p>
      </div>

      {/* KPI 카드 4개 */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={<DollarSign className="text-emerald-600" />}
          label="총 매출"
          value={formatKrw(netRevenue)}
          sub={`충전 ${formatKrw(stats.purchaseRevenueKrw)} + 수수료 ${formatKrw(stats.paymentFeeRevenueKrw)}`}
          accent="emerald"
        />
        <KpiCard
          icon={<TrendingUp className="text-blue-600" />}
          label="크레딧 사용량"
          value={formatNum(stats.creditsUsed)}
          sub={`환급 ${formatNum(stats.creditsRefunded)} 차감 후`}
          accent="blue"
        />
        <KpiCard
          icon={<TrendingDown className="text-amber-600" />}
          label="크레딧 지급"
          value={formatNum(stats.creditsGranted)}
          sub="무료 지급 + 환급 + 관리자 조정"
          accent="amber"
        />
        <KpiCard
          icon={<Repeat className="text-purple-600" />}
          label="구매 크레딧"
          value={formatNum(stats.creditsPurchased)}
          sub={`평균 단가 ₩${Math.round(stats.purchaseRevenueKrw / Math.max(stats.creditsPurchased, 1))}`}
          accent="purple"
        />
      </div>

    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  KPI 카드
 * ───────────────────────────────────────────────────────────────── */
const ACCENTS: Record<string, string> = {
  emerald: 'bg-emerald-50',
  blue: 'bg-blue-50',
  amber: 'bg-amber-50',
  purple: 'bg-purple-50',
};

function KpiCard({
  icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  accent: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-gray-500">{label}</div>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${ACCENTS[accent]}`}>
          {icon}
        </div>
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
      <div className="mt-1 truncate text-xs text-gray-500">{sub}</div>
    </div>
  );
}
